// ─── Theme ───
export const colors = {
  bg: "#0a0f1a",
  surface: "#111827",
  surfaceAlt: "#1a2235",
  border: "#1e293b",
  borderLight: "#334155",
  text: "#e2e8f0",
  textMuted: "#94a3b8",
  textDim: "#64748b",
  accent: "#3b82f6",
  accentDim: "rgba(59,130,246,0.1)",
  success: "#10b981",
  danger: "#ef4444",
  warning: "#f59e0b",
};

export const font = `'DM Sans', sans-serif`;

// ─── Nav ───
// Single source of truth for the sidebar, the Home screen cards, and page titles.
// `group`: "main" = records the team works in and returns to; "tools" = utilities run
// occasionally. `desc` powers the Home card blurb so the card list is never duplicated.
export const NAV_ITEMS = [
  { id: "home", label: "Home", icon: "⬡", group: "main", desc: "Overview of everything the team is tracking" },
  { id: "calendar", label: "Calendar", icon: "◫", group: "main", desc: "Team events, conferences, and out-of-office tracking" },
  { id: "employees", label: "My Tasks", icon: "☰", group: "main", desc: "Your tasks and workload across clients and internal work" },
  { id: "clients", label: "Clients", icon: "◇", group: "main", desc: "Client engagements, objectives, POCs, and running notes" },
  { id: "internal", label: "Sawtooth Internal", icon: "◎", group: "main", desc: "Internal objectives and who is working on what" },
  { id: "proposals", label: "Proposal Tracker", icon: "◈", group: "main", desc: "Track government contract proposals through submission and award lifecycle" },
  { id: "contacts", label: "Contacts", icon: "◆", group: "main", desc: "Shared contact book — everyone the team has met, and which clients are working them" },
  { id: "discussion", label: "Discussion Items", icon: "◧", group: "main", desc: "Track discussion topics and action items for team meetings" },
  { id: "opsplanner", label: "Ops Planner", icon: "▦", group: "accountowners", desc: "Project timelines and Gantt across clients" },
  { id: "templates", label: "Task Templates", icon: "▤", group: "accountowners", desc: "Reusable objectives and task lists to stamp onto projects" },
  { id: "meetingtemplates", label: "Meeting Templates", icon: "▥", group: "accountowners", adminOnly: true, desc: "The Kickoff meeting template stamped onto new Kickoff meetings" },
  { id: "dashboard", label: "Dashboard", icon: "▧", group: "accountowners", desc: "Pipeline, tasks, projects and capacity at a glance" },
  { id: "frontdoor", label: "Front Door Programs", icon: "◈", group: "tools", desc: "Front Door program tracking" },
  { id: "onepager", label: "One-Pager Builder", icon: "◱", group: "tools", desc: "Generate defense-industry one-pagers" },
  { id: "pdf-markdown", label: "PDF → Markdown", icon: "↧", group: "tools", desc: "Convert PDFs to clean markdown" },
];

// Convenience selectors so callers never re-filter inline.
export const MAIN_NAV = NAV_ITEMS.filter((n) => n.group === "main");
export const ACCOUNT_NAV = NAV_ITEMS.filter((n) => n.group === "accountowners");
export const TOOL_NAV = NAV_ITEMS.filter((n) => n.group === "tools");
export const navItem = (id) => NAV_ITEMS.find((n) => n.id === id);

// Account Owners nav for this user: admin-only items (e.g. Meeting Templates) are hidden from
// everyone else. A UI convenience only — roles are not a server boundary in this app.
export const accountNavFor = (user) => ACCOUNT_NAV.filter((n) => !n.adminOnly || user?.role === "admin");

// ─── Proposal Types ───
export const PROPOSAL_TYPES = [
  "SBIR Phase I", "SBIR Phase II", "STTR Phase I", "STTR Phase II",
  "BAA", "RFP", "RFI", "OTA", "Other",
];

export const BRANCHES = ["Air Force", "Army", "Navy", "Marine Corps", "Space Force", "DHS", "DOE", "DARPA", "Other"];

export const STATUS_OPTIONS = [
  { value: "Submitted", color: colors.accent },
  { value: "Not Selected", color: colors.danger },
  { value: "Selected not Funded", color: "#fb923c" },
  { value: "Funded", color: colors.success },
];

// ─── Calendar Event Types ───
export const EVENT_TYPES = [
  { id: "bd-conference", label: "BD Conference", color: "#8b5cf6", bg: "rgba(139,92,246,0.12)", border: "rgba(139,92,246,0.35)" },
  { id: "client-conference", label: "Client Conference", color: "#10b981", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.35)" },
  { id: "ooo", label: "Out of Office", color: "#f59e0b", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.35)" },
  { id: "important", label: "Important Event", color: "#ef4444", bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.35)" },
  { id: "pro-dev", label: "Professional Development", color: "#3b82f6", bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.35)" },
  { id: "holiday", label: "Holiday", color: "#14b8a6", bg: "rgba(20,184,166,0.12)", border: "rgba(20,184,166,0.35)" },
];

// ─── Discussion Categories ───
export const DISCUSSION_CATEGORIES = [
  { id: "decision", label: "Decision Needed", color: "#f59e0b", bg: "rgba(245,158,11,0.1)" },
  { id: "blocker", label: "Blocker", color: "#ef4444", bg: "rgba(239,68,68,0.1)" },
  { id: "update", label: "Update", color: "#3b82f6", bg: "rgba(59,130,246,0.1)" },
  { id: "idea", label: "Idea", color: "#a78bfa", bg: "rgba(167,139,250,0.1)" },
  { id: "fyi", label: "FYI", color: "#67e8f9", bg: "rgba(103,232,249,0.1)" },
];

export const FRONT_DOOR_BRANCHES = [
  "Army", "Navy", "Air Force", "Marines", "Space Force",
  "Joint", "DHS", "DoE", "Other",
];

// ─── Helpers ───
export function generateId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function formatCurrency(val) {
  if (!val) return "$0";
  return "$" + Number(val).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

export function formatDate(d) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function getDefaultResponseDate(submissionDate) {
  if (!submissionDate) return "";
  const d = new Date(submissionDate + "T00:00:00");
  d.setMonth(d.getMonth() + 2);
  return d.toISOString().split("T")[0];
}

export function isOverdue(proposal) {
  if (!proposal.expectedResponse || ["Funded", "Not Selected", "Selected not Funded"].includes(proposal.status)) return false;
  return new Date(proposal.expectedResponse + "T00:00:00") < new Date();
}

export function displayName(user) {
  if (!user) return "Unknown";
  return user.firstName || user.name?.split(" ")[0] || user.email?.split("@")[0] || "Unknown";
}

export function fullName(user) {
  if (!user) return "Unknown";
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
  return user.name || user.email || "Unknown";
}

export function getEventType(id) {
  return EVENT_TYPES.find((t) => t.id === id) || EVENT_TYPES[EVENT_TYPES.length - 1];
}
